/**
 * WHAT THE PARTS OF THE VISIT SCREEN SHARE, and none of it draws anything: the 2-up field rhythm,
 * what a recorded change comes back as, a text held by ops's go-live switch among it (ops #31), the
 * three rules that read a change, the two that read where its message is, and the one line its
 * reminder before the clean is said in (ops #78). And, since ops #76 O6, where a client email stands
 * in the office's words (`emailStanding`), which the invoice screen and the household's Emails card
 * read too, and the reminder's line by email beside the text's.
 */
import {
  FAILURE_CODE_LABELS,
  REFUSAL_CODES,
  heldByEmailSwitch,
  humanise,
  type MessageIntentState,
} from '@fk/shared';
import type {
  ChangeResult,
  ChangeRow,
  EmailGoLive,
  MessageIntentRow,
  OfficeEmailRow,
  OfficeVisitDetail,
  ReminderRow,
  SmsGoLive,
} from '../../api.ts';
import { choiceWords } from '../../choices.ts';
import { formatInstant, longDay } from '../../format.ts';
import {
  EMAIL_STANDING_LABELS,
  EMAIL_WITHDRAWN_BECAUSE,
  EMAIL_WITHDRAWN_EXPLAINED,
  HOME_EMAIL_SAID,
  REMINDER_STATE_LABELS,
  REMINDER_WITHDRAWN_BECAUSE,
  type EmailStanding,
} from '../../labels.ts';

/** The 2-up field rhythm the artboard uses inside a card and inside the change panel. */
export const PAIR = 'md:grid md:grid-cols-2 md:gap-x-3';

export type Outcome =
  | { readonly tone: 'saved'; readonly text: string }
  | { readonly tone: 'waiting'; readonly text: string }
  | { readonly tone: 'refused'; readonly text: string };

/**
 * THE CHANGE THAT IS STILL WAITING, if there is one. Told and silenced are the two ways a change
 * finishes, and a change with neither is the one the server refuses a second change behind.
 *
 * Newest first is the order the API sends, and the newest waiting one is the one to resolve: it is
 * the only one there can be, because the server refuses to write a second while the first stands.
 */
export function waitingChange(changes: readonly ChangeRow[]): ChangeRow | null {
  return changes.find((change) => change.toldAt === null && change.silenceReason === null) ?? null;
}

/**
 * The three states a message is still on its way in. `composed` and `approved` are ops's own, and
 * `handed_off` is the phone system holding it; everything after that is an answer rather than a
 * wait. A message in any of the three is the reason nobody at this desk has to do anything: the
 * visit moves when delivery is reported.
 */
const ON_ITS_WAY: ReadonlySet<MessageIntentState> = new Set<MessageIntentState>([
  'composed',
  'approved',
  'handed_off',
]);

export function messageOnItsWay(change: ChangeRow): MessageIntentRow | null {
  return change.intents.find((intent) => ON_ITS_WAY.has(intent.state)) ?? null;
}

/** A message that will never arrive: refused by the register, or failed at the phone system. */
export function messageThatFailed(change: ChangeRow): MessageIntentRow | null {
  return (
    change.intents.find(
      (intent) => intent.state === 'refused_by_consent' || intent.state === 'failed',
    ) ?? null
  );
}

const REFUSED: ReadonlySet<string> = new Set(REFUSAL_CODES);

/**
 * WHY THE PHONE SYSTEM SAYS IT DID NOT GO, as the sentence the panel adds (ops #23), or null when
 * it gave no word. A refusal code is the phone system refusing the instruction ops sent; any other
 * is a message it took and could not deliver. The two read differently because the person does
 * different things about them: a refusal is ops's to put right, a failure is the number's or the
 * carrier's. "Did not go" alone reads the same for both.
 *
 * AND A TEXT OPS'S OWN SWITCH HELD NEVER REACHED THE PHONE SYSTEM AT ALL (ops #31), so it is not
 * the phone system's word and the sentence does not send the office there. The composer, and the
 * drain once the switch moved, write it failed `not_enabled` with a refused time and nothing handed
 * over. The refused time is the mark: only ops writes it, and a `not_enabled` the phone system
 * reports on a lost receipt leaves the handed-off time empty too.
 *
 * AN EMAIL OPS'S SWITCH FOR CLIENT EMAIL HELD never reached home (ops #76 O6), which delivers every
 * email, so it is said of that switch and of home, by the channel the intent names.
 *
 * Read of the four fields it needs and its channel, and no more, so a household's thread of texts
 * (ops #26, part 2), which hands in no channel since every row of it is a text, says it in the same
 * words as the visit's page.
 */
export function whyItDidNotGo(intent: WhyItDidNotGo): string | null {
  if (intent.state !== 'failed' || intent.failureCode === null) return null;
  if (heldByOps(intent)) {
    return intent.channel === 'email'
      ? 'Ops held it, so it never reached home: client email was off in ops, or on for test addresses only and their address was not one of them. An owner sets the email switch in Settings.'
      : 'Ops held it, so it never reached the phone system: texting was off in ops, or on for test handsets only and their number was not one of them. An owner sets the texting switch in Settings.';
  }
  const label = FAILURE_CODE_LABELS[intent.failureCode];
  const words = `${label.charAt(0).toLowerCase()}${label.slice(1)}`;
  return REFUSED.has(intent.failureCode)
    ? `The phone system refused it: ${words}.`
    : `The phone system could not send it: ${words}.`;
}

/**
 * WHAT TO DO ABOUT A TEXT THAT DID NOT GO, where there is one thing to do (ops #33), or null. A text
 * the phone system failed `number_held` stays held, and so does every text after it, until the
 * office confirms the number and ops asks the phone system to release it: Number confirmed, on her
 * household's thread. Every other code is the why alone.
 */
export function whatToDoAbout(
  intent: Pick<MessageIntentRow, 'state' | 'failureCode'>,
): string | null {
  return intent.state === 'failed' && intent.failureCode === 'number_held'
    ? 'Call them to confirm the number.'
    : null;
}

/**
 * What `whyItDidNotGo` reads of a message: where it stands, the code, the two moments, and its
 * channel where the caller has one, a text where it has none.
 */
type WhyItDidNotGo = Pick<MessageIntentRow, 'state' | 'failureCode' | 'refusedAt' | 'handedOffAt'> &
  Partial<Pick<MessageIntentRow, 'channel'>>;

/** A failed text ops's go-live switch held (ops #31): `not_enabled`, refused here, never handed over. */
function heldByOps(intent: WhyItDidNotGo): boolean {
  return (
    intent.failureCode === 'not_enabled' && intent.refusedAt !== null && intent.handedOffAt === null
  );
}

/**
 * WHAT `emailStanding` READS OF AN EMAIL (ops #76 O6): where ops left it, and what home reported of it.
 * The invoice screen's emails and the household's card hand in every field; a reminder on the visit
 * page hands in what its read carries, and a field it does not carry reads as nothing reported.
 */
export interface EmailFacts {
  readonly state: MessageIntentState;
  readonly failureCode: OfficeEmailRow['failureCode'];
  readonly createdAt: string;
  readonly refusedAt: string | null;
  readonly handedOffAt: string | null;
  readonly cancelledAt: string | null;
  readonly deliveredAt?: string | null;
  readonly acceptedAt?: string | null;
  readonly failedAt?: string | null;
  readonly bouncedLateAt?: string | null;
  readonly homeSaid?: string | null;
  readonly parkedWith?: string | null;
  readonly waitingForPdf?: boolean;
  /** It waits for her door at home (`docs/plan-clients-only-home.md` section 3). */
  readonly waitingForDoor?: boolean;
  /**
   * What it was, `invoice_sent` and the rest, which says what the office does once one parked for her
   * door: send an invoice email's link afresh, and for any other ask for no link (`doorAgain`). Absent
   * where the read does not carry it, which asks for no link.
   */
  readonly kind?: string;
}

/** Where an email stands: which of the states, its short label, and the sentence beside it. */
export interface EmailStandingLine {
  readonly standing: EmailStanding;
  readonly label: string;
  readonly sentence: string;
}

/** The register's own prefix on a refusal, which the office's words leave off. */
const REGISTER_PREFIX = 'refused: ';

/** The words a push parked for its PDF ends with (`WAITED_PAST_ITS_END` on the server). */
const WAITED_FOR_ITS_PDF = 'expired: it waited for its PDF';

/**
 * The words a push parked waiting for her door at home ends with (`WAITED_FOR_HER_DOOR` on the server,
 * `docs/plan-clients-only-home.md` section 3), which no word of home's is. A row parked before
 * 2026-10-20 says "her door": the words became neutral then, and the stored ones were never rewritten.
 */
const WAITED_FOR_THE_DOOR = [
  'expired: it waited for their door',
  'expired: it waited for her door',
];

/**
 * WHAT THE OFFICE DOES ONCE AN EMAIL PARKED FOR HER DOOR, as the drain's task for it says
 * (`EXPIRED_WAITING` in `api/src/seams/outbox.ts`): she is invited to home again from the household,
 * and then an invoice email's link is sent afresh. Every other email at home, a receipt, a payment
 * problem or a past-due note, carries no link the office sends again (D8), so its words ask for none
 * and offer the call, as its task does. Chosen by its kind, as the drain chooses the task's words.
 */
function doorAgain(kind: string | undefined): string {
  return kind === 'invoice_sent'
    ? 'Invite them to home again from the household, then send them a fresh link.'
    : 'Invite them to home again from the household, or call them.';
}

/** The words a push held for its words is parked with (`heldWordsError` on the server). */
const HELD_FOR_ITS_WORDS = 'invalid: held at composing';

/** An instant as the office reads it, or the stored value where it is not one. */
const when = (instant: string): string => formatInstant(instant) ?? instant;

/**
 * HELD FOR ITS WORDS (`docs/plan-o6-o9-emails.md` 3.1): the writer failed it `invalid` at composing,
 * refused here and never handed to home, so home never reported on it. Told by its parked words where
 * the read carries them, and by that shape where it does not, the visit page's reminder; never the
 * switch for client email, which fails one `not_enabled`, and never home's `kind_not_known`, which
 * reaches ops after home took it.
 */
function heldForItsWords(email: EmailFacts): boolean {
  if (email.state !== 'failed' || email.failureCode !== 'invalid') return false;
  if (email.parkedWith) return email.parkedWith.startsWith(HELD_FOR_ITS_WORDS);
  return email.refusedAt !== null && email.handedOffAt === null && !email.failedAt;
}

/** Why a withdrawn email was withdrawn, finishing "Withdrawn", or nothing where the record does not say. */
function withdrawnBecause(parkedWith: string | null | undefined): string {
  if (!parkedWith) return '';
  const known = EMAIL_WITHDRAWN_BECAUSE[parkedWith];
  if (known) return ` ${known}`;
  return parkedWith.startsWith('withdrawn: ') ? ` ${EMAIL_WITHDRAWN_EXPLAINED}` : '';
}

/**
 * WHERE AN EMAIL STANDS, IN THE OFFICE'S WORDS (ops #76 O6, `docs/plan-o6-o9-emails.md` section 3.7,
 * "What the office sees"), `whyItDidNotGo`'s neighbour: one helper for the invoice screen's emails, the
 * household's Emails card and the visit page's reminder, so one email is never said two ways. In the
 * order the facts decide it: a bounce after delivery; refused by the register, with what the register
 * said, never that she did not agree when a stop or a mark refused her; withdrawn, with why; held by
 * the switch for client email; held for its words, which is never the switch; did not go, with home's
 * word; delivered; accepted by Microsoft; with home; waiting for her door at home, and for its PDF
 * beside it where both wait; waiting for its PDF; waiting to go.
 */
export function emailStanding(email: EmailFacts): EmailStandingLine {
  const line = (standing: EmailStanding, sentence: string): EmailStandingLine => ({
    standing,
    label: EMAIL_STANDING_LABELS[standing],
    sentence,
  });
  if (email.bouncedLateAt) {
    return line(
      'bounced_late',
      `Bounced after delivery, ${when(email.bouncedLateAt)}. Call them, and check the address on their record.`,
    );
  }
  switch (email.state) {
    case 'refused_by_consent': {
      const said = email.parkedWith?.startsWith(REGISTER_PREFIX)
        ? email.parkedWith.slice(REGISTER_PREFIX.length)
        : null;
      return line(
        'refused',
        said ? `Not sent. The register refused it: ${said}.` : 'Not sent. The register refused it.',
      );
    }
    case 'cancelled':
      return line('withdrawn', `Withdrawn${withdrawnBecause(email.parkedWith)}.`);
    case 'failed': {
      if (heldByOps(email))
        return line('held_by_switch', whyItDidNotGo({ ...email, channel: 'email' })!);
      if (heldForItsWords(email)) {
        return line(
          'held_words',
          'Its words were held for the office to look at, so nothing went to home, and a task says why.',
        );
      }
      if (email.parkedWith?.startsWith(WAITED_FOR_ITS_PDF)) {
        return line(
          'did_not_go',
          `Did not go: ${email.parkedWith.slice('expired: '.length)}. Send them a fresh link.`,
        );
      }
      const parkedWith = email.parkedWith;
      if (parkedWith && WAITED_FOR_THE_DOOR.some((words) => parkedWith.startsWith(words))) {
        return line(
          'did_not_go',
          `Did not go: ${parkedWith.slice('expired: '.length)}. ${doorAgain(email.kind)}`,
        );
      }
      // Their own choice, or the office's hold (ops #76 phase 2): parked by ops, in its words.
      if (parkedWith?.startsWith('not sent: ')) {
        return line('did_not_go', `Not sent: ${choiceWords(parkedWith)}`);
      }
      if (email.homeSaid) {
        const said = HOME_EMAIL_SAID[email.homeSaid] ?? `home said ${email.homeSaid}`;
        return line('did_not_go', `Did not go: ${said}.`);
      }
      return line('did_not_go', 'Did not go.');
    }
    case 'delivered':
      return line(
        'delivered',
        email.deliveredAt
          ? `Delivered: home confirmed it ${when(email.deliveredAt)}.`
          : 'Delivered.',
      );
    case 'handed_off':
      if (email.acceptedAt) {
        return line(
          'accepted',
          `Accepted by Microsoft ${when(email.acceptedAt)}. Home confirms delivery about a day after acceptance.`,
        );
      }
      return line(
        'with_home',
        email.handedOffAt
          ? `With home since ${when(email.handedOffAt)}, to be sent from home@.`
          : 'With home, to be sent from home@.',
      );
    case 'composed':
    case 'approved':
      if (email.waitingForDoor) {
        return line(
          'waiting_for_door',
          email.waitingForPdf
            ? 'Waiting for their door at home and for its PDF: home is handed it once home holds their door and the PDF is made.'
            : 'Waiting for their door at home: home is handed it once home holds their door.',
        );
      }
      if (email.waitingForPdf) {
        return line(
          'waiting_for_pdf',
          'Waiting for its PDF: home is handed it once the PDF is made.',
        );
      }
      return line('waiting', `Written ${when(email.createdAt)}, waiting to be handed to home.`);
  }
}

/**
 * The instant the phone system's texting hours release a text it holds, while that is still ahead
 * of `now`, or null. Once the hour has come the hold is over, and saying "until" a time already
 * past would be wrong about where the message is.
 */
export function heldUntil(
  intent: Pick<MessageIntentRow, 'state' | 'sendAfter'>,
  now: number,
): string | null {
  if (intent.state !== 'handed_off' || intent.sendAfter === null) return null;
  const at = Date.parse(intent.sendAfter);
  return Number.isFinite(at) && at > now ? intent.sendAfter : null;
}

/** The reminder's one line: a short label, the sentence beside it, and the words she gets, if any. */
export interface ReminderLine {
  readonly label: string;
  readonly sentence: string;
  /** The reminder's words, when there is one standing for the day the visit is on. */
  readonly body: string | null;
}

/** The two states a visit is reminded in, as the job reads them. */
const REMINDED_STATUSES: ReadonlySet<string> = new Set(['planned', 'confirmed']);

/** Where a reminder that stands for the visit's day is, in a sentence. */
function whereItStands(reminder: ReminderRow, now: number): string {
  switch (reminder.state) {
    case 'composed':
    case 'approved':
      return `Written ${formatInstant(reminder.createdAt) ?? ''}, waiting to be handed to the phone system.`;
    case 'handed_off': {
      const held = heldUntil(reminder, now);
      if (held) return `Handed over, and held by the texting hours until ${formatInstant(held)}.`;
      return reminder.handedOffAt
        ? `Handed over ${formatInstant(reminder.handedOffAt)}.`
        : 'Handed over.';
    }
    case 'delivered':
      return reminder.deliveredAt
        ? `Delivered ${formatInstant(reminder.deliveredAt)}.`
        : 'Delivered.';
    case 'failed': {
      const why = whyItDidNotGo(reminder);
      return why ? `Did not go. ${why}` : 'Did not go.';
    }
    case 'refused_by_consent':
      return 'Not sent: the register refused it.';
    case 'cancelled':
      return 'Withdrawn when the visit changed.';
  }
}

/**
 * WHY NO REMINDER WILL GO, OR WENT, for a visit that is open and is reminded by text, as the rest of a
 * sentence, or null when nothing in the record stands in its way. In the job's own order: the news
 * that comes first, the person, the switch, and the register.
 */
function whatStandsInTheWay(detail: OfficeVisitDetail, gate: SmsGoLive | undefined): string | null {
  const facts = detail.reminder!;
  if (waitingChange(detail.changes)) {
    return 'a change on this visit is still waiting to be told, and its message is the news';
  }
  if (facts.askUndecided) {
    return 'an ask from home about this visit is still undecided, and the answer is the news';
  }
  if (facts.changedSinceMoment) {
    return 'the visit was booked or changed inside the lead time, so its own message or the reason typed for it was the news';
  }
  const primary = detail.contacts.find((contact) => contact.role === 'primary');
  if (!primary || facts.register === null) return 'the household has no main contact';
  if (!primary.phoneE164) return 'the main contact has no number';
  if (!gate || gate.state === 'off') return 'texting is off in ops';
  if (gate.state === 'test_handsets' && !gate.handsets.includes(primary.phoneE164)) {
    return 'texting is on for test handsets only, and their number is not one of them';
  }
  if (facts.register.mark) return 'their number is marked do not contact';
  if (facts.register.hold) return 'a stop in their own words is waiting for the office';
  if (!facts.register.sms) {
    return 'their number has not ticked the box on the website form, or they said STOP';
  }
  return null;
}

/** Where the next reminder stands for the day the visit is on, with none written for it yet. */
function nextReminder(
  detail: OfficeVisitDetail,
  gate: SmsGoLive | undefined,
  now: number,
): Omit<ReminderLine, 'body'> {
  const facts = detail.reminder!;
  const { visit } = detail;
  if (facts.moment === null) {
    return {
      label: 'Off',
      sentence: 'Visit reminders are off. An owner turns them on in Settings.',
    };
  }
  if (detail.agreement.serviceLine === 'home_watch') {
    return { label: 'None', sentence: 'A home watch visit is never reminded by text.' };
  }
  if (!REMINDED_STATUSES.has(visit.status)) {
    return {
      label: 'None',
      sentence: `None now: the visit is ${humanise(visit.status).toLowerCase()}.`,
    };
  }
  const blocked = whatStandsInTheWay(detail, gate);
  if (Date.parse(facts.moment) > now) {
    const to = detail.contacts.find((contact) => contact.role === 'primary');
    return {
      label: 'Not yet due',
      sentence: blocked
        ? `Due ${formatInstant(facts.moment)}, but as things stand none will go: ${blocked}.`
        : `Due ${formatInstant(facts.moment)}, by text to ${to?.displayName ?? 'the main contact'}.`,
    };
  }
  if (blocked) return { label: 'None', sentence: `None went: ${blocked}.` };
  if (Date.parse(facts.start) <= now) {
    return { label: 'None', sentence: 'None went before the clean began.' };
  }
  return {
    label: 'Due',
    sentence: `Due since ${formatInstant(facts.moment)}. The next run inside the texting hours writes it.`,
  };
}

/**
 * THE VISIT'S REMINDER BEFORE THE CLEAN IN ONE LINE (ops #78, `docs/plan-visit-reminder.md` section
 * 8), or null from a Worker older than the field. The reminder that stands for the day the visit is
 * on says where it is. With none, a withdrawn one says so first, and why: the visit changed, or a
 * STOP, a mark, a hold or a forget on her number with the visit where it was. Then when the next is
 * due under the settings as they stand, or why none will go or went, from what the page reads: the
 * facts the server sent beside the reminders, the household, the changes, and ops's texting switch
 * from `GET /api/me`. Nothing here is a control: no reminder is approved or withdrawn by hand.
 */
export function reminderLine(
  detail: OfficeVisitDetail,
  gate: SmsGoLive | undefined,
  now: number,
): ReminderLine | null {
  if (!detail.reminder) return null;
  // Its texts alone: a reminder by email is `emailReminderLine`'s (ops #76 O6).
  const reminders = (detail.reminders ?? []).filter((one) => one.channel !== 'email');
  const day = detail.visit.scheduledDate;
  const current = reminders.find(
    (one) => one.state !== 'cancelled' && (one.day === null || one.day === day),
  );
  if (current) {
    return {
      label: REMINDER_STATE_LABELS[current.state],
      sentence: whereItStands(current, now),
      body: current.body,
    };
  }
  const next = nextReminder(detail, gate, now);
  const earlier = reminders[0];
  if (!earlier) return { ...next, body: null };
  const named = earlier.day ? ` for ${longDay(earlier.day)}` : '';
  // Why it was withdrawn, as the server read it from where it was parked: a change, or a stop on her
  // number with the visit where it was. A Worker that does not say, or a reason this page does not
  // know, claims none rather than a wrong one.
  const because = earlier.withdrawnFor
    ? (REMINDER_WITHDRAWN_BECAUSE[earlier.withdrawnFor] as string | undefined)
    : undefined;
  const before =
    earlier.state === 'cancelled'
      ? `The reminder${named} was withdrawn${because ? ` ${because}` : ''}.`
      : `The reminder${named} was for the day the visit stood on before it moved.`;
  return {
    label:
      earlier.state === 'cancelled' && next.label === 'None'
        ? REMINDER_STATE_LABELS.cancelled
        : next.label,
    sentence: `${before} ${next.sentence}`,
    body: null,
  };
}

/** The spelling home reads an address in, so the list of test addresses and her record need not agree. */
const spelled = (address: string): string => address.trim().toLowerCase();

/**
 * WHY NO REMINDER WILL GO, OR WENT, BY EMAIL (ops #76 O6), as the rest of a sentence, or null when
 * nothing in the record stands in its way. In the job's own order (`systemVisitsDueAReminder`): the
 * news that comes first, the person and her address, the switch for client email, and the register by
 * email. An ask from home undecided stands in no email's way: the email goes, saying she asked and the
 * office will confirm, where the text waits.
 */
function whatStandsInTheEmailsWay(
  detail: OfficeVisitDetail,
  mail: EmailGoLive | undefined,
): string | null {
  const facts = detail.reminder!;
  if (waitingChange(detail.changes)) {
    return 'a change on this visit is still waiting to be told, and its message is the news';
  }
  if (facts.changedSinceMoment) {
    return 'the visit was booked or changed inside the lead time, so its own message or the reason typed for it was the news';
  }
  const primary = detail.contacts.find((contact) => contact.role === 'primary');
  const register = facts.emailRegister ?? null;
  if (!primary || register === null) return 'the household has no main contact';
  if (!primary.email) return 'the main contact has no email address';
  if (!mail || mail.state === 'off') return 'client email is off in ops';
  if (
    mail.state === 'test_addresses' &&
    !mail.addresses.map(spelled).includes(spelled(primary.email))
  ) {
    return 'client email is on for test addresses only, and their address is not one of them';
  }
  if (register.mark) return 'they or their address is marked do not contact for email';
  if (register.stop) return 'they asked not to be emailed';
  if (!register.email) return 'there is no yes to email on their record';
  return null;
}

/** Where the next reminder by email stands for the day the visit is on, with none written for it yet. */
function nextEmailReminder(
  detail: OfficeVisitDetail,
  mail: EmailGoLive | undefined,
  now: number,
): Omit<ReminderLine, 'body'> {
  const facts = detail.reminder!;
  const { visit } = detail;
  if (facts.moment === null) {
    return {
      label: 'Off',
      sentence: 'Visit reminders are off. An owner turns them on in Settings.',
    };
  }
  if (!REMINDED_STATUSES.has(visit.status)) {
    return {
      label: 'None',
      sentence: `None now: the visit is ${humanise(visit.status).toLowerCase()}.`,
    };
  }
  const blocked = whatStandsInTheEmailsWay(detail, mail);
  if (Date.parse(facts.moment) > now) {
    const to = detail.contacts.find((contact) => contact.role === 'primary');
    return {
      label: 'Not yet due',
      sentence: blocked
        ? `Due ${formatInstant(facts.moment)}, but as things stand none will go: ${blocked}.`
        : `Due ${formatInstant(facts.moment)}, by email to ${to?.displayName ?? 'the main contact'}.`,
    };
  }
  if (blocked) return { label: 'None', sentence: `None went: ${blocked}.` };
  if (Date.parse(facts.start) <= now) {
    return { label: 'None', sentence: 'None went before the clean began.' };
  }
  return {
    label: 'Due',
    sentence: `Due since ${formatInstant(facts.moment)}. The next run writes it.`,
  };
}

/**
 * THE VISIT'S REMINDER BY EMAIL IN ONE LINE (ops #76 O6, `docs/plan-o6-o9-emails.md` section 3.7), the
 * text's twin, or null where there is nothing to say by email: a Worker older than the fields, or a
 * main contact with no address and no email reminder ever written for the visit, which is how every
 * visit read before client email reads. The reminder that stands for the day the visit is on says
 * where it is, in the words every email is said in (`emailStanding`); with none, a withdrawn one says
 * so first, and why; then when the next is due, or why none will go or went, by the job's own rules
 * for an email: no texting hours, every service line, an ask from home no bar. Nothing to press.
 */
export function emailReminderLine(
  detail: OfficeVisitDetail,
  mail: EmailGoLive | undefined,
  now: number,
): ReminderLine | null {
  if (!detail.reminder || detail.reminder.emailRegister === undefined) return null;
  const emails = (detail.reminders ?? []).filter((one) => one.channel === 'email');
  const primary = detail.contacts.find((contact) => contact.role === 'primary');
  if (emails.length === 0 && !primary?.email) return null;
  const day = detail.visit.scheduledDate;
  const current = emails.find(
    (one) => one.state !== 'cancelled' && (one.day === null || one.day === day),
  );
  if (current) {
    const standing = emailStanding(current);
    return { label: standing.label, sentence: standing.sentence, body: current.body };
  }
  const next = nextEmailReminder(detail, mail, now);
  const earlier = emails[0];
  if (!earlier) return { ...next, body: null };
  const named = earlier.day ? ` for ${longDay(earlier.day)}` : '';
  const because = earlier.withdrawnFor
    ? (REMINDER_WITHDRAWN_BECAUSE[earlier.withdrawnFor] as string | undefined)
    : undefined;
  const before =
    earlier.state === 'cancelled'
      ? `The reminder${named} was withdrawn${because ? ` ${because}` : ''}.`
      : `The reminder${named} was for the day the visit stood on before it moved.`;
  return {
    label:
      earlier.state === 'cancelled' && next.label === 'None'
        ? REMINDER_STATE_LABELS.cancelled
        : next.label,
    sentence: `${before} ${next.sentence}`,
    body: null,
  };
}

/** One line of the reminder, and the channel it is about, or null where it is about both. */
export interface ChannelReminderLine {
  readonly channel: 'text' | 'email' | null;
  readonly line: ReminderLine;
}

/**
 * THE REMINDER BY TEXT AND BY EMAIL (ops #76 O6), as the visit page draws it: the text's line alone
 * where there is nothing to say by email, as before; one line where both say the same, or where both
 * are due alike, "by text and by email"; and otherwise a line for each, named. Null from a Worker older
 * than the reminder's fields.
 */
export function reminderLines(
  detail: OfficeVisitDetail,
  sms: SmsGoLive | undefined,
  mail: EmailGoLive | undefined,
  now: number,
): readonly ChannelReminderLine[] | null {
  const text = reminderLine(detail, sms, now);
  if (!text) return null;
  const email = emailReminderLine(detail, mail, now);
  if (!email) return [{ channel: null, line: text }];
  if (text.label === email.label && text.sentence === email.sentence && text.body === email.body) {
    return [{ channel: null, line: text }];
  }
  const byEmail = email.sentence.replace(', by email to ', ', by text to ');
  if (
    text.label === email.label &&
    text.body === null &&
    email.body === null &&
    byEmail !== email.sentence &&
    byEmail === text.sentence
  ) {
    return [
      {
        channel: null,
        line: {
          ...text,
          sentence: text.sentence.replace(', by text to ', ', by text and by email to '),
        },
      },
    ];
  }
  return [
    { channel: 'text', line: text },
    { channel: 'email', line: email },
  ];
}

export function outcomeFor(result: ChangeResult): Outcome {
  if (result.applied) return { tone: 'saved', text: 'Recorded and moved.' };
  if (result.refusedByConsent) {
    return {
      tone: 'refused',
      text: 'They have not agreed to messages on that channel, so nothing was sent. Ring them, and record what they agreed to on their household page.',
    };
  }
  // Ops's own go-live switch held it (ops #31), or client email's held an email (ops #76 O6): written,
  // parked, and the change left open.
  if (result.gateRefusal) {
    return {
      tone: 'refused',
      text: heldByEmailSwitch(result.gateRefusal)
        ? 'Nothing was sent: client email is not on in ops for their address, so the visit has not moved. Ring them, then say how they already know.'
        : 'Nothing was sent: texting is not on in ops for their number, so the visit has not moved. Ring them, then say how they already know.',
    };
  }
  // Their own choice, or the office's hold (ops #76 phase 2): written, parked, and the change left open.
  if (result.choiceRefusal) {
    return {
      tone: 'refused',
      text: `Nothing was sent: ${choiceWords(result.choiceRefusal)} The visit has not moved. ${
        result.officeCalls
          ? 'With email and texts off for this, a task gives the office the call: ring them, then say they were told by phone.'
          : 'Ring them, or tell them another way, then say how they already know.'
      }`,
    };
  }
  return { tone: 'waiting', text: 'Recorded. The visit moves when the message is delivered.' };
}
